import { OperationsMetricsDetailsPageProps } from '../../types/api'
import { TIME } from '../../utils/misc'
import { isCompany, isStore } from '../../utils/location'
import {
    getGreenfieldDrillValues,
    getGreenfieldLocationFilter,
} from '../../utils/greenfield'
import { QueryKeys } from '../../constants/query-keys'
import { fetchOperationsMetricsDetails } from '../../queries/operations/dimensions'
import { useQuery } from '@tanstack/react-query'
import { useAnalytics } from 'utils/analytics'

export function useOperationMetricsDetails({
    env,
    accessToken,
    location,
    card,
    timeDimension,
    filters,
}: OperationsMetricsDetailsPageProps) {
    const { tenMinutes } = TIME
    const { trackEvent } = useAnalytics()
    const isCompanyLocation = isCompany(location)
    const isStoreLocation = isStore(location)

    const formattedTimeDimension = timeDimension
        ?.trim()
        .toLowerCase()
        .replace(/\s+/g, '_')

    const greenfieldFilters = {
        ...(isStoreLocation && getGreenfieldLocationFilter(location)),
        ...(!isStoreLocation &&
            !isCompanyLocation &&
            getGreenfieldDrillValues(location)),
        time_dimension: formattedTimeDimension, // Use the formatted time dimension
        ...(filters ?? {}),
    }

    const isCardValid = !!card?.trim()

    const detailedQuery = {
        queryKey: [
            QueryKeys.OPERATIONSMETRICSDETAILS,
            location,
            card,
            formattedTimeDimension,
            filters,
        ],
        queryFn: async () => {
            if (!isCardValid) throw new Error('Card is missing')
            return fetchOperationsMetricsDetails({
                env,
                accessToken,
                dynamicSubPath: card,
                method: 'GET',
                filters: greenfieldFilters,
                trackEvent,
            })
        },
        staleTime: tenMinutes,
        cacheTime: tenMinutes,
        retry: false,
        enabled: isCardValid,
    }

    const { data, isLoading, isError, refetch, remove, error } =
        useQuery(detailedQuery)

    return { data, isLoading, isError, refetch, remove, error }
}
